import { animate, AnimationBuilder, AnimationPlayer, style } from '@angular/animations';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
  ViewEncapsulation,
  booleanAttribute,
  effect,
  inject,
  input,
  model,
  output,
  viewChild,
  viewChildren
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { getNavigationList } from '@core/sidebar.menu';
import { AccessType } from '@shared/enums/auth.enum';
import { ApiResponse } from '@shared/model/ApiResponse.model';
import { BrandFactoryConnectionService } from '@shared/service/brand/brand-factory-connection.service';
import { FactoryAddressService } from '@shared/service/factory/factory-address.service';
import { FactoryMachineService } from '@shared/service/factory/factory-machine.service';
import { MachineChangeTrackService } from '@shared/service/factory-machine-change-track.service';
import { YearChangeTrackService } from '@shared/service/factory-year-change-track.service';
import { HoverTrackService } from '@shared/service/hover-track.service';
import {
  getAdminRoutePaths,
  getAllowedAdminRoutes,
  isAdminRouteAllowed
} from '@shared/utils/admin-permission-route.util';
import { CurrentUserQuery } from '@state/auth-state/current-user.query';
import { PermissionQuery } from '@state/permission-state/permission.query';
import { combineLatest, merge, ReplaySubject, Subject, Subscription } from 'rxjs';
import { delay, filter, takeUntil } from 'rxjs/operators';

import { fuseAnimations } from '../../../animations/public-api';
import { FuseScrollbarDirective } from '../../../directives/scrollbar/scrollbar.directive';
import { FuseUtilsService } from '../../../services/utils/utils.service';
import { FuseNavigationService } from '../navigation.service';
import {
  FuseNavigationItem,
  FuseVerticalNavigationAppearance,
  FuseVerticalNavigationMode,
  FuseVerticalNavigationPosition
} from '../navigation.types';

import { FuseVerticalNavigationAsideItemComponent } from './components/aside/aside.component';
import { FuseVerticalNavigationBasicItemComponent } from './components/basic/basic.component';
import { FuseVerticalNavigationCollapsableItemComponent } from './components/collapsable/collapsable.component';
import { FuseVerticalNavigationDividerItemComponent } from './components/divider/divider.component';
import { FuseVerticalNavigationGroupItemComponent } from './components/group/group.component';
import { FuseVerticalNavigationSpacerItemComponent } from './components/spacer/spacer.component';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'fuse-vertical-navigation',
  templateUrl: './vertical.component.html',
  styleUrls: ['./vertical.component.scss'],
  animations: fuseAnimations,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'fuseVerticalNavigation',
  imports: [
    FuseScrollbarDirective,
    FuseVerticalNavigationAsideItemComponent,
    FuseVerticalNavigationBasicItemComponent,
    FuseVerticalNavigationCollapsableItemComponent,
    FuseVerticalNavigationDividerItemComponent,
    FuseVerticalNavigationGroupItemComponent,
    FuseVerticalNavigationSpacerItemComponent
  ]
})
export class FuseVerticalNavigationComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
  private readonly destroyRef = inject(DestroyRef);
  private _animationBuilder = inject(AnimationBuilder);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _elementRef = inject(ElementRef);
  private _renderer2 = inject(Renderer2);
  private _router = inject(Router);
  private _scrollStrategyOptions = inject(ScrollStrategyOptions);
  private _fuseNavigationService = inject(FuseNavigationService);
  private _fuseUtilsService = inject(FuseUtilsService);
  private __hoverTrackService = inject(HoverTrackService);
  private readonly currentUserQuery = inject(CurrentUserQuery);
  private readonly factoryMachineService = inject(FactoryMachineService);
  private readonly factoryAddressService = inject(FactoryAddressService);
  private readonly yearChangeTrackService = inject(YearChangeTrackService);
  private readonly machineChangeTrackService = inject(MachineChangeTrackService);
  private readonly permissionQuery = inject(PermissionQuery);
  private readonly brandFactoryConnectionService = inject(BrandFactoryConnectionService);
  readonly appearance = input<FuseVerticalNavigationAppearance>('default');
  readonly autoCollapse = input<boolean>(true);
  readonly inner = input<boolean, BooleanInput>(false, { transform: booleanAttribute });
  readonly mode = input<FuseVerticalNavigationMode>('side');
  readonly name = input<string>(this._fuseUtilsService.randomId());
  readonly opened = model<boolean>(true);
  readonly position = input<FuseVerticalNavigationPosition>('left');
  readonly transparentOverlay = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute
  });
  readonly appearanceChanged = output<FuseVerticalNavigationAppearance>();
  readonly modeChanged = output<FuseVerticalNavigationMode>();
  readonly openedChanged = output<boolean>();
  readonly positionChanged = output<FuseVerticalNavigationPosition>();
  private readonly _navigationContentEl = viewChild<ElementRef>('navigationContent');
  private readonly _fuseScrollbarDirectives = viewChildren(FuseScrollbarDirective);
  navigation: FuseNavigationItem[] = [];
  activeAsideItemId: string | null = null;
  onCollapsableItemCollapsed: ReplaySubject<FuseNavigationItem> =
    new ReplaySubject<FuseNavigationItem>(1);
  onCollapsableItemExpanded: ReplaySubject<FuseNavigationItem> =
    new ReplaySubject<FuseNavigationItem>(1);
  onRefreshed: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private _animationsEnabled: boolean = false;
  private _asideOverlay: HTMLElement;
  private readonly _handleAsideOverlayClick: any;
  private readonly _handleOverlayClick: any;
  private _hovered: boolean = false;
  private _overlay: HTMLElement;
  private _player: AnimationPlayer;
  private _scrollStrategy: ScrollStrategy = this._scrollStrategyOptions.block();
  private _fuseScrollbarDirectivesSubscription: Subscription;
  private _unsubscribeAll: Subject<void> = new Subject<void>();
  private permissionData: any = null;
  private currentOrganizationId: string;
  private _currentGroupId: string;
  private isYearChange: boolean;
  private viewAs: any;
  private userData: any;
  private accessType: any;
  private accessRole: any;
  private factorySubscriptionPlan: any = {};
  isDemoBrand: boolean = false;
  isMachineChanged: boolean;
  hasOrder: any;
  factoryYears: any[] = [];
  /**
   * Constructor
   */
  constructor() {
    this._handleAsideOverlayClick = (): void => {
      this.closeAside();
    };
    this._handleOverlayClick = (): void => {
      this.close();
    };
    effect(() => {
      const directives = this._fuseScrollbarDirectives();
      if (directives.length === 0) {
        return;
      }
      if (this._fuseScrollbarDirectivesSubscription) {
        this._fuseScrollbarDirectivesSubscription.unsubscribe();
      }
      this._fuseScrollbarDirectivesSubscription = merge(
        this.onCollapsableItemCollapsed,
        this.onCollapsableItemExpanded
      )
        .pipe(takeUntil(this._unsubscribeAll), delay(250))
        .subscribe(() => {
          directives.forEach(fuseScrollbarDirective => {
            fuseScrollbarDirective.update();
          });
        });
    });
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------
  /**
   * Host binding for component classes
   */
  @HostBinding('class') get classList(): any {
    return {
      'fuse-vertical-navigation-animations-enabled': this._animationsEnabled,
      [`fuse-vertical-navigation-appearance-${this.appearance()}`]: true,
      'fuse-vertical-navigation-hover': this._hovered,
      'fuse-vertical-navigation-inner': this.inner(),
      'fuse-vertical-navigation-mode-over': this.mode() === 'over',
      'fuse-vertical-navigation-mode-side': this.mode() === 'side',
      'fuse-vertical-navigation-opened': this.opened(),
      'fuse-vertical-navigation-position-left': this.position() === 'left',
      'fuse-vertical-navigation-position-right': this.position() === 'right'
    };
  }
  /**
   * Host binding for component inline styles
   */
  @HostBinding('style') get styleList(): any {
    return {
      visibility: this.opened() ? 'visible' : 'hidden'
    };
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  /**
   * On changes
   *
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    // Appearance
    if ('appearance' in changes) {
      // Execute the observable
      this.appearanceChanged.emit(changes.appearance.currentValue);
    }
    // Mode
    const opened = this.opened();
    if ('mode' in changes) {
      // Get the previous and current values
      const currentMode = changes.mode.currentValue;
      const previousMode = changes.mode.previousValue;
      // Disable the animations
      this._disableAnimations();
      // If the mode changes: 'over -> side'
      if (previousMode === 'over' && currentMode === 'side') {
        // Hide the overlay
        this._hideOverlay();
      }
      // If the mode changes: 'side -> over'
      if (previousMode === 'side' && currentMode === 'over') {
        // Close the aside
        this.closeAside();
        // If the navigation is opened
        if (opened) {
          // Show the overlay
          this._showOverlay();
        }
      }
      // Execute the observable
      this.modeChanged.emit(currentMode);
      // Enable the animations after a delay
      // The delay must be bigger than the current transition-duration
      // to make sure nothing will be animated while the mode changing
      setTimeout(() => {
        this._enableAnimations();
        this._changeDetectorRef.markForCheck();
      }, 500);
    }
    // Navigation
    if ('navigation' in changes) {
      // Mark for check
      this._changeDetectorRef.markForCheck();
    }
    // Opened
    if ('opened' in changes) {
      // Coerce the value to a boolean
      const open = coerceBooleanProperty(changes.opened.currentValue);
      // Open/close the navigation
      this._toggleOpened(open);
    }
    // Position
    if ('position' in changes) {
      // Execute the observable
      this.positionChanged.emit(changes.position.currentValue);
    }
  }
  /**
   * On init
   */
  ngOnInit(): void {
    this.yearChangeTrack();
    this.machineChangeTrack();
    // Register the navigation component
    const name = this.name() || this._fuseUtilsService.randomId();
    this._fuseNavigationService.registerComponent(name, this);
    // Subscribe to the 'NavigationEnd' event
    this._router.events
      .pipe(
        filter((event: any) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        // If the mode is 'over' and the navigation is opened...
        const mode = this.mode();
        if (mode === 'over' && this.opened()) {
          // Close the navigation
          this.close();
        }
        // If the mode is 'side' and the aside is active...
        if (mode === 'side' && this.activeAsideItemId) {
          this.closeAside();
        }
        this._changeDetectorRef.markForCheck();
      });
    this.getDataFromState();
  }
  getFactoryMachine(): Promise<any> {
    return new Promise(resolve => {
      this.factoryMachineService
        .getAllWithFilters({
          factory: this.currentOrganizationId,
          hideList: true
        })
        .subscribe((res: any) => {
          resolve(res.data);
        });
    });
  }
  // FIXME: this function was not called with Group ID
  getFactoryBrandConnection(): Promise<any> {
    return new Promise(resolve => {
      this.brandFactoryConnectionService
        .getAllBrandFactoryConnectionWithFilters({
          factory: this.currentOrganizationId,
          status: 'ACCEPTED'
        })
        .subscribe((res: ApiResponse) => {
          resolve(res.data);
        });
    });
  }
  async buildNavigationForFactory(): Promise<void> {
    let navigation = JSON.parse(
      JSON.stringify(getNavigationList(this.accessType, false, this.viewAs))
    );
    if (localStorage.getItem('factoryAddress') == null) {
      const factoryAddresses = await this.getFactoryAddress();
      const mainFactoryAddress = factoryAddresses.find(address => address?.isMainAddress === true);

      if (mainFactoryAddress) {
        localStorage.setItem('factoryAddress', JSON.stringify(mainFactoryAddress));
      }
    }
    if (this.factoryYears.length <= 1) {
      this.lockKpiCompareMenu(navigation);
    }
    if (this.accessRole === 'user' && this.accessType !== 'admin') {
      this.updateFactoryUserPermission(navigation);
    }
    if (this.accessType !== 'admin') {
      navigation = this.filterMenuForSubscriptionPlan(navigation);
    }
    this.navigation = navigation;
    this._changeDetectorRef.markForCheck();
  }
  getFactoryAddress(): Promise<any[]> {
    if (!this.currentOrganizationId) {
      return Promise.resolve([]);
    }

    return new Promise(resolve => {
      this.factoryAddressService
        .getAllWithFilters({
          factory: this.currentOrganizationId
        })
        .subscribe((res: ApiResponse) => {
          resolve(Array.isArray(res.data) ? res.data : []);
        });
    });
  }
  buildNavigationForAdmin() {
    const navigation = JSON.parse(JSON.stringify(getNavigationList(this.accessType)));
    if (this.accessRole == 'user') {
      this.navigation = this.updateAdminUserPermission(navigation);
    } else {
      this.navigation = navigation;
    }
  }
  filterMenuForSubscriptionPlan(navigation: FuseNavigationItem[]): any {
    if (this.factorySubscriptionPlan != null) {
      const updatedNavigation: FuseNavigationItem[] = [];
      navigation.forEach(menuItem => {
        if (menuItem.id === 'rtis-mcp') {
          updatedNavigation.push(menuItem);
          return;
        }
        if (menuItem.id === 'home') {
          menuItem.children.forEach(item => {
            if (!this.factorySubscriptionPlan[item.id]) {
              this.lockSubscriptionMenuItem(item);
            }
          });
        } else if (!this.factorySubscriptionPlan[menuItem.id]) {
          this.lockSubscriptionMenuItem(menuItem);
        }
        updatedNavigation.push(menuItem);
      });
      return updatedNavigation;
    } else {
      return navigation;
    }
  }

  lockSubscriptionMenuItem(item: FuseNavigationItem): void {
    item.title = item.title?.replace('🔒', '');
    item.tooltip = 'Upgrade subscription plan to unlock';
    item.badge = {
      title: '🔒',
      classes: 'bg-slate-200 text-slate-700 px-1.5 rounded-sm'
    };
    item.link = null;
    item.disabled = true;

    item.children?.forEach(child => this.lockSubscriptionMenuItem(child));
  }

  buildNavigationForGroup(): void {
    if (this.viewAs === 'factory') {
      this.buildNavigationForFactory();
    } else {
      this.navigation = getNavigationList(this.accessType, false, this.viewAs);
    }
  }
  async buildNavigation(): Promise<any> {
    if (this.viewAs === undefined) {
      this.buildNavigationForAdmin();
    } else {
      if (this.viewAs === 'from_admin_to_factory') {
        this.buildNavigationForFactory();
      } else if (this.viewAs === 'from_admin_to_brand') {
        this.buildNavigationForBrand();
      } else if (this.viewAs === 'from_admin_to_group') {
        this.buildNavigationForGroup();
      } else if (
        this.viewAs === 'from_admin_to_group_factory' ||
        this.viewAs === 'from_admin_to_group_factories'
      ) {
        this.buildNavigationForFactory();
      } else {
        this.buildNavigationForConsultant();
      }
    }
  }
  async buildNavigationForBrand(_currentUser?: any): Promise<void> {
    const navigation = getNavigationList(this.accessType, false, this.viewAs, this.accessRole);
    this.navigation = navigation;
    if (!this.hasOrder) {
      this.navigation = navigation.filter(item => item.id !== 'order');
    }
  }
  buildNavigationForConsultant(): void {
    let navigation = getNavigationList(this.accessType, false, this.viewAs, this.accessRole);
    if (this.accessType === 'consultant' && this.accessRole === 'user') {
      navigation = this.filterConsultantUserNavigation(navigation);
    }
    this.navigation = navigation;
  }
  lockVerificationMenu(navigation: FuseNavigationItem[]): any[] {
    const verificationIndex = navigation.findIndex((item: any) => item.id === 'verification');
    if (verificationIndex !== -1) {
      const verification = navigation[verificationIndex];
      verification.title = 'Brand Data Verification 🔒';
      verification.link = '';
      verification.tooltip = 'Minimum 1 brand connection needed';
    }
    return navigation;
  }
  lockKpiCompareMenu(navigation: FuseNavigationItem[]): any[] {
    const homeIndex = navigation.findIndex((item: any) => item.id === 'home');
    if (homeIndex !== -1) {
      const kpiCompareIndex = navigation[homeIndex]['children'].findIndex(
        (item: any) => item.id === 'kpi-compare'
      );
      if (kpiCompareIndex !== -1) {
        const kpiCompare = navigation[homeIndex]['children'][kpiCompareIndex];
        kpiCompare.title = 'KPI Compare 🔒';
        kpiCompare.link = '';
        kpiCompare.tooltip = 'Minimum 2 years data required';
      }
    }
    return navigation;
  }
  lockMaintenanceScheduleListMenu(navigation: FuseNavigationItem[]): any[] {
    const homeIndex = navigation.findIndex((item: any) => item.id === 'home');
    if (homeIndex !== -1) {
      const maintenanceScheduleIndex = navigation[homeIndex]['children'].findIndex(
        (item: any) => item.id === 'maintenance-schedule'
      );
      if (maintenanceScheduleIndex !== -1) {
        const maintenanceSchedule = navigation[homeIndex]['children'][maintenanceScheduleIndex];
        maintenanceSchedule.title = 'Maintenance Schedule 🔒';
        maintenanceSchedule.link = '';
        maintenanceSchedule.tooltip = 'No machine added';
      }
    }
    return navigation;
  }
  updateAdminUserPermission(navigation: any[]): any[] {
    if (this.permissionData?.isAdmin) {
      return navigation;
    }
    const perms = this.permissionData?.permissions?.scope;
    if (!perms || Object.keys(perms).length === 0) return navigation;
    const adminRoutes = getAdminRoutePaths(this._router.config);
    const allowedRoutes = Object.keys(perms).flatMap(permissionKey =>
      perms[permissionKey] ? getAllowedAdminRoutes(permissionKey, adminRoutes) : []
    );
    if (!allowedRoutes.length) {
      return [];
    }
    return navigation.filter(menu => {
      if (menu.children) {
        menu.children = this.updateAdminUserPermission(menu.children);
        return menu.children.length > 0;
      }
      return isAdminRouteAllowed(menu.link, allowedRoutes);
    });
  }
  updateFactoryUserPermission(navigation: FuseNavigationItem[]): void {
    if (
      this.permissionData?.permissions?.scope.DATA_ENTRY_MODULES === undefined ||
      !this.permissionData?.permissions?.scope.DATA_ENTRY_MODULES.length
    ) {
      this.removeDataEntryPermission(navigation);
      this.removeDataExportPermission(navigation);
    }
    if (!this.permissionData?.permissions?.scope.PROJECTS?.length) {
      this.removeProjectPermission(navigation);
    }
    if (!this.permissionData?.permissions?.scope.IMPLEMENTATION_PLANS) {
      this.removeImplementationPlanPermission(navigation);
    }
    if (!this.permissionData?.permissions?.scope.MAINTENANCE_SCHEDULE) {
      this.removeMaintenanceSchedulePermission(navigation);
    }
    this.removeConfigurationPermission(navigation);
    this.removeSetupPermission(navigation);
    this.removeDashboardPermission(navigation);
  }
  updateConsultantUserPermission(navigation: FuseNavigationItem[]): FuseNavigationItem[] {
    return this.filterConsultantUserNavigation(navigation);
  }
  filterConsultantUserNavigation(navigation: FuseNavigationItem[]): FuseNavigationItem[] {
    const projectFactories = this.getConsultantProjectFactoryPermissions();
    const hasProjectPermission = projectFactories.some(
      (item: any) => item?.projectType === 'PROJECT'
    );
    const hasAuditPermission = projectFactories.some(
      (item: any) => item?.projectType === 'ONLINE_AUDIT'
    );
    return navigation.filter((item: any) => {
      if (item.id === 'project') {
        return hasProjectPermission;
      }
      if (item.id === 'audit') {
        return hasAuditPermission;
      }
      return true;
    });
  }
  private getConsultantProjectFactoryPermissions(): any[] {
    const projectFactories =
      this.userData?.permissions?.scope?.PROJECT_FACTORIES ||
      this.permissionData?.permissions?.scope?.PROJECT_FACTORIES;
    return Array.isArray(projectFactories) ? projectFactories : [];
  }
  removeDataEntryPermission(navigation: FuseNavigationItem[]): void {
    const dataEntryIndex = navigation.findIndex((data: any) => data.id === 'data-entry');
    if (dataEntryIndex > -1) {
      navigation.splice(dataEntryIndex, 1);
    }
  }
  removeDataExportPermission(navigation: FuseNavigationItem[]): FuseNavigationItem[] | undefined {
    const dataExportIndex = navigation.findIndex((data: any) => data.id === 'data-export');
    if (dataExportIndex > -1) {
      navigation.splice(dataExportIndex, 1);
      return navigation;
    }
  }
  removeProjectPermission(navigation: FuseNavigationItem[]): FuseNavigationItem[] | undefined {
    const project = navigation.findIndex((data: any) => data.id === 'project');
    if (project > -1) {
      navigation.splice(project, 1);
      return navigation;
    }
  }
  removeDashboardPermission(navigation: FuseNavigationItem[]): FuseNavigationItem[] | undefined {
    const dashboard = navigation.findIndex((data: any) => data.id === 'dashboard');
    if (dashboard > -1) {
      navigation.splice(dashboard, 1);
      return navigation;
    }
  }
  removeImplementationPlanPermission(
    navigation: FuseNavigationItem[]
  ): FuseNavigationItem[] | undefined {
    const homeIndex = navigation.findIndex((data: any) => data.id === 'home');
    if (homeIndex > -1) {
      const implementationPlanIndex = navigation[homeIndex].children.findIndex(
        (data: any) => data.id === 'implementation-plan'
      );
      if (implementationPlanIndex > -1) {
        navigation[1].children.splice(implementationPlanIndex, 1);
        return navigation;
      }
    }
  }
  removeMaintenanceSchedulePermission(
    navigation: FuseNavigationItem[]
  ): FuseNavigationItem[] | undefined {
    const homeIndex = navigation.findIndex((data: any) => data.id === 'home');
    if (homeIndex > -1) {
      const maintenanceScheduleIndex = navigation[homeIndex].children.findIndex(
        (data: any) => data.id === 'maintenance-schedule'
      );
      if (maintenanceScheduleIndex > -1) {
        navigation[1].children.splice(maintenanceScheduleIndex, 1);
        return navigation;
      }
    }
  }
  removeConfigurationPermission(
    navigation: FuseNavigationItem[]
  ): FuseNavigationItem[] | undefined {
    const configurationIndex = navigation.findIndex((data: any) => data.id === 'configuration');
    if (configurationIndex > -1) {
      navigation.splice(configurationIndex, 1);
      return navigation;
    }
  }
  removeSetupPermission(navigation: FuseNavigationItem[]): FuseNavigationItem[] | undefined {
    const setupIndex = navigation.findIndex((data: any) => data.id === 'setup');
    if (setupIndex > -1) {
      navigation.splice(setupIndex, 1);
      return navigation;
    }
  }
  removeSettingPermission(navigation: FuseNavigationItem[]): FuseNavigationItem[] | undefined {
    const setupIndex = navigation.findIndex((data: any) => data.id === 'settings');
    if (setupIndex > -1) {
      navigation.splice(setupIndex, 1);
      return navigation;
    }
  }
  yearChangeTrack(): void {
    this.yearChangeTrackService.yearChangeTrack.subscribe((res: boolean) => {
      this.isYearChange = res;
      if (this.isYearChange) {
        this.buildNavigationForFactory();
      }
      this._changeDetectorRef.markForCheck();
    });
  }
  machineChangeTrack(): void {
    this.machineChangeTrackService.machineChangeTrack.subscribe((res: boolean) => {
      this.isMachineChanged = res;
      if (this.isMachineChanged) {
        this.buildNavigationForFactory();
      }
      this._changeDetectorRef.markForCheck();
    });
  }
  /**
   * After view init
   */
  ngAfterViewInit(): void {
    setTimeout(() => {
      // Return if 'navigation content' element does not exist
      const _navigationContentEl = this._navigationContentEl();
      if (!_navigationContentEl) {
        return;
      }
      // If 'navigation content' element doesn't have
      // perfect scrollbar activated on it...
      if (!_navigationContentEl.nativeElement.classList.contains('ps')) {
        // Find the active item
        const activeItem = _navigationContentEl.nativeElement.querySelector(
          '.fuse-vertical-navigation-item-active'
        );
        // If the active item exists, scroll it into view
        if (activeItem) {
          activeItem.scrollIntoView();
        }
      }
      // Otherwise
      else {
        // Go through all the scrollbar directives
        this._fuseScrollbarDirectives().forEach(fuseScrollbarDirective => {
          // Skip if not enabled
          if (!fuseScrollbarDirective.isEnabled()) {
            return;
          }
          // Scroll to the active element
          fuseScrollbarDirective.scrollToElement(
            '.fuse-vertical-navigation-item-active',
            -120,
            true
          );
        });
      }
      this._changeDetectorRef.markForCheck();
    });
  }
  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Forcefully close the navigation and aside in case they are opened
    this.close();
    this.closeAside();
    // Deregister the navigation component from the registry
    this._fuseNavigationService.deregisterComponent(this.name());
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------
  /**
   * Refresh the component to apply the changes
   */
  refresh(): void {
    // Mark for check
    this._changeDetectorRef.markForCheck();
    // Execute the observable
    this.onRefreshed.next(true);
  }
  /**
   * Open the navigation
   */
  open(): void {
    // Return if the navigation is already open
    if (this.opened()) {
      return;
    }
    // Set the opened
    this._toggleOpened(true);
  }
  /**
   * Close the navigation
   */
  close(): void {
    // Return if the navigation is already closed
    if (!this.opened()) {
      return;
    }
    // Close the aside
    this.closeAside();
    // Set the opened
    this._toggleOpened(false);
  }
  /**
   * Toggle the navigation
   */
  toggle(): void {
    // Toggle
    if (this.opened()) {
      this.close();
    } else {
      this.open();
    }
  }
  /**
   * Open the aside
   *
   * @param item
   */
  openAside(item: FuseNavigationItem): void {
    // Return if the item is disabled
    if (item.disabled || !item.id) {
      return;
    }
    // Open
    this.activeAsideItemId = item.id;
    // Show the aside overlay
    this._showAsideOverlay();
    // Mark for check
    this._changeDetectorRef.markForCheck();
  }
  /**
   * Close the aside
   */
  closeAside(): void {
    // Close
    this.activeAsideItemId = null;
    // Hide the aside overlay
    this._hideAsideOverlay();
    // Mark for check
    this._changeDetectorRef.markForCheck();
  }
  /**
   * Toggle the aside
   *
   * @param item
   */
  toggleAside(item: FuseNavigationItem): void {
    // Toggle
    if (this.activeAsideItemId === item.id) {
      this.closeAside();
    } else {
      this.openAside(item);
    }
  }
  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------
  /**
   * Enable the animations
   *
   * @private
   */
  private _enableAnimations(): void {
    // Return if the animations are already enabled
    if (this._animationsEnabled) {
      return;
    }
    // Enable the animations
    this._animationsEnabled = true;
  }
  /**
   * Disable the animations
   *
   * @private
   */
  private _disableAnimations(): void {
    // Return if the animations are already disabled
    if (!this._animationsEnabled) {
      return;
    }
    // Disable the animations
    this._animationsEnabled = false;
  }
  /**
   * Show the overlay
   *
   * @private
   */
  private _showOverlay(): void {
    // Return if there is already an overlay
    if (this._asideOverlay) {
      return;
    }
    // Create the overlay element
    this._overlay = this._renderer2.createElement('div');
    // Add a class to the overlay element
    this._overlay.classList.add('fuse-vertical-navigation-overlay');
    // Add a class depending on the transparentOverlay option
    if (this.transparentOverlay()) {
      this._overlay.classList.add('fuse-vertical-navigation-overlay-transparent');
    }
    // Append the overlay to the parent of the navigation
    this._renderer2.appendChild(this._elementRef.nativeElement.parentElement, this._overlay);
    // Enable block scroll strategy
    this._scrollStrategy.enable();
    // Create the enter animation and attach it to the player
    this._player = this._animationBuilder
      .build([animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 1 }))])
      .create(this._overlay);
    // Play the animation
    this._player.play();
    // Add an event listener to the overlay
    this._overlay.addEventListener('click', this._handleOverlayClick);
  }
  /**
   * Hide the overlay
   *
   * @private
   */
  private _hideOverlay(): void {
    if (!this._overlay) {
      return;
    }
    // Create the leave animation and attach it to the player
    this._player = this._animationBuilder
      .build([animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 0 }))])
      .create(this._overlay);
    // Play the animation
    this._player.play();
    // Once the animation is done...
    this._player.onDone(() => {
      // If the overlay still exists...
      if (this._overlay) {
        // Remove the event listener
        this._overlay.removeEventListener('click', this._handleOverlayClick);
        // Remove the overlay
        this._overlay.parentNode.removeChild(this._overlay);
        this._overlay = null;
      }
      // Disable block scroll strategy
      this._scrollStrategy.disable();
    });
  }
  /**
   * Show the aside overlay
   *
   * @private
   */
  private _showAsideOverlay(): void {
    // Return if there is already an overlay
    if (this._asideOverlay) {
      return;
    }
    // Create the aside overlay element
    this._asideOverlay = this._renderer2.createElement('div');
    // Add a class to the aside overlay element
    this._asideOverlay.classList.add('fuse-vertical-navigation-aside-overlay');
    // Append the aside overlay to the parent of the navigation
    this._renderer2.appendChild(this._elementRef.nativeElement.parentElement, this._asideOverlay);
    // Create the enter animation and attach it to the player
    this._player = this._animationBuilder
      .build([animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 1 }))])
      .create(this._asideOverlay);
    // Play the animation
    this._player.play();
    // Add an event listener to the aside overlay
    this._asideOverlay.addEventListener('click', this._handleAsideOverlayClick);
  }
  /**
   * Hide the aside overlay
   *
   * @private
   */
  private _hideAsideOverlay(): void {
    if (!this._asideOverlay) {
      return;
    }
    // Create the leave animation and attach it to the player
    this._player = this._animationBuilder
      .build([animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 0 }))])
      .create(this._asideOverlay);
    // Play the animation
    this._player.play();
    // Once the animation is done...
    this._player.onDone(() => {
      // If the aside overlay still exists...
      if (this._asideOverlay) {
        // Remove the event listener
        this._asideOverlay.removeEventListener('click', this._handleAsideOverlayClick);
        // Remove the aside overlay
        this._asideOverlay.parentNode.removeChild(this._asideOverlay);
        this._asideOverlay = null;
      }
    });
  }
  /**
   * Open/close the navigation
   *
   * @param open
   * @private
   */
  private _toggleOpened(open: boolean): void {
    // Set the opened
    this.opened.set(open);
    // Enable the animations
    this._enableAnimations();
    // If the navigation opened, and the mode
    // is 'over', show the overlay
    if (this.mode() === 'over') {
      if (this.opened()) {
        this._showOverlay();
      } else {
        this._hideOverlay();
      }
    }
    // Execute the observable
    this.openedChanged.emit(open);
  }
  private getDataFromState(): void {
    const userQuery$ = this.currentUserQuery.select();
    const permissionQuery$ = this.permissionQuery.select();
    combineLatest([userQuery$, permissionQuery$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([userQuery, permissionQuery]) => {
        this.userData = userQuery.data;
        // Logout resets the store to { data: {} }; skip the empty-user emission
        // so the role/type reads below never hit `undefined.toLowerCase()`.
        if (!this.userData?.userType) {
          return;
        }
        if (
          this.userData?.userType === AccessType.FACTORY ||
          this.userData?.viewAs === 'from_admin_to_factory'
        ) {
          this.userData?.userType === AccessType.FACTORY
            ? this.userData.organization?.factorySubscriptionPlan?.subscriptionPlan?.listOfFeatures?.forEach(
                (features: string) => {
                  this.factorySubscriptionPlan[features] = true;
                }
              )
            : this.userData.organization?.factorySubscriptionPlan?.subscriptionPlan?.listOfFeatures?.forEach(
                (features: string) => {
                  this.factorySubscriptionPlan[features] = true;
                }
              );
          this.factoryYears = this.userData.organization.factoryYears;
        }
        this.permissionData = permissionQuery.data;
        this.viewAs = userQuery.data.viewAs;
        this.accessType = this.userData?.userType?.toLowerCase();
        this.accessRole = this.userData?.userRole?.toLowerCase();
        if (this.accessType === 'brand') {
          this.hasOrder = this.userData.hasOrderForBrandUser;
        }
        if (this.accessType === 'admin') {
          this._currentGroupId = this.userData?.group?.id;
          this.currentOrganizationId = this.userData?.organization?.id;
        } else {
          this.currentOrganizationId = this.userData?.organization?.id;
        }
        if (this.accessType === 'factory') {
          this.buildNavigationForFactory();
        } else if (this.accessType === 'group_of_factory') {
          this.buildNavigationForGroup();
        } else if (this.accessType === 'brand') {
          this.buildNavigationForBrand(this.userData);
        } else if (this.accessType === 'admin') {
          this.buildNavigation();
        } else if (this.accessType === 'association') {
          const navigation = getNavigationList(
            this.accessType,
            false,
            this.viewAs,
            this.accessRole
          );
          this.navigation = navigation;
        } else {
          this.buildNavigationForConsultant();
        }
        this._changeDetectorRef.markForCheck();
      });
  }
}
